// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * ReUseX's typed building model (`rux create solids`, ReUseX PR #94) as
 * `ElementTally[]` and visibility filters for the "Bygningsdele" panel
 * (frontend-t02) — the web counterpart of the Qt client's
 * `visualize::count_building_elements()` / `ElementFilter`.
 *
 * Walls, openings, spaces, floor slabs and roof come straight from
 * `GET /walls`, `/openings`, `/spaces` (ruxd#11) and `/slabs` (ruxd-jmt) —
 * already fetched by `useBuildingModel` / `useServerSpatial`, so this
 * module adds no network call of its own. Pure: no React, no DOM.
 */

import type { SolidOpening, SolidSlab, SolidSpace, SolidWall } from '../api/types';
import type { ModelKind } from '../bygning/modelLayers';
import { isElementVisible, type ElementFilterState, type ElementTally } from './elementFilter';

function bump(n: Map<string, number>, key: string): void {
  n.set(key, (n.get(key) ?? 0) + 1);
}

function talliesFromKeyedCounts(category: string, n: ReadonlyMap<string, number>): ElementTally[] {
  return [...n].map(([storeyKey, count]) => ({ category, storeyId: storeyKey === 'null' ? null : Number(storeyKey), count }));
}

function wallTallies(walls: readonly SolidWall[] | null | undefined): ElementTally[] {
  const n = new Map<string, number>();
  for (const w of walls ?? []) bump(n, String(w.storey_id));
  return talliesFromKeyedCounts('wall', n);
}

/** `SolidOpening.kind` → the panel's category key ("Døre" / "Vinduer" / "Andre åbninger"). */
export function openingCategory(kind: SolidOpening['kind']): string {
  return kind === 'door' ? 'door' : kind === 'window' ? 'window' : 'opening';
}

/** An opening's storey: its own `storey_id`, falling back to its host wall's when the opening carries none. */
export function openingStoreyId(opening: Pick<SolidOpening, 'storey_id' | 'wall_guid'>, wallStoreyByGuid: ReadonlyMap<string, number | null>): number | null {
  const own = opening.storey_id;
  if (own !== null && own !== undefined) return own;
  return wallStoreyByGuid.get(opening.wall_guid) ?? null;
}

function openingTallies(openings: readonly SolidOpening[] | null | undefined, wallStoreyByGuid: ReadonlyMap<string, number | null>): ElementTally[] {
  const n = new Map<string, number>();
  for (const o of openings ?? []) bump(n, `${openingCategory(o.kind)}\u0000${openingStoreyId(o, wallStoreyByGuid)}`);
  const out: ElementTally[] = [];
  for (const [key, count] of n) {
    const sep = key.indexOf('\u0000');
    const category = key.slice(0, sep);
    const storeyKey = key.slice(sep + 1);
    out.push({ category, storeyId: storeyKey === 'null' ? null : Number(storeyKey), count });
  }
  return out;
}

function spaceTallies(spaces: readonly SolidSpace[] | null | undefined): ElementTally[] {
  const n = new Map<string, number>();
  for (const s of spaces ?? []) bump(n, String(s.storey_id));
  return talliesFromKeyedCounts('space', n);
}

/** `SolidSlab.kind` → the panel's category key ("Dæk" / "Tag"). */
export function slabCategory(kind: SolidSlab['kind']): string {
  return kind === 'roof' ? 'roof' : 'slab';
}

/** Floor slab and roof counts per storey, from `GET /slabs`. */
export function slabTallies(slabs: readonly SolidSlab[] | null | undefined): ElementTally[] {
  const n = new Map<string, number>();
  for (const s of slabs ?? []) bump(n, `${slabCategory(s.kind)}\u0000${s.storey_id}`);
  const out: ElementTally[] = [];
  for (const [key, count] of n) {
    const sep = key.indexOf('\u0000');
    const storeyKey = key.slice(sep + 1);
    out.push({ category: key.slice(0, sep), storeyId: storeyKey === 'null' ? null : Number(storeyKey), count });
  }
  return out;
}

/** A `guid → storey_id` lookup for `openingStoreyId`. */
export function wallStoreysByGuid(walls: readonly SolidWall[] | null | undefined): Map<string, number | null> {
  const m = new Map<string, number | null>();
  for (const w of walls ?? []) m.set(w.guid, w.storey_id);
  return m;
}

export interface BuildingElementSource {
  walls: readonly SolidWall[] | null | undefined;
  openings: readonly SolidOpening[] | null | undefined;
  spaces: readonly SolidSpace[] | null | undefined;
  slabs: readonly SolidSlab[] | null | undefined;
}

/** Every category's tallies (ReUseX's typed model, the "Bygningsdele" rows), for the panel and the viewer. */
export function buildingElementTallies(source: BuildingElementSource): ElementTally[] {
  const byGuid = wallStoreysByGuid(source.walls);
  return [...wallTallies(source.walls), ...openingTallies(source.openings, byGuid), ...spaceTallies(source.spaces), ...slabTallies(source.slabs)];
}

/** `source.walls`, kept to the categories and storey the filter shows. */
export function visibleWalls(walls: readonly SolidWall[] | null | undefined, state: ElementFilterState): SolidWall[] {
  return (walls ?? []).filter((w) => isElementVisible(state, 'wall', w.storey_id));
}

/** `source.openings`, kept to the categories and storey the filter shows (storey resolved via the host wall). */
export function visibleOpenings(
  openings: readonly SolidOpening[] | null | undefined,
  wallStoreyByGuid: ReadonlyMap<string, number | null>,
  state: ElementFilterState,
): SolidOpening[] {
  return (openings ?? []).filter((o) => isElementVisible(state, openingCategory(o.kind), openingStoreyId(o, wallStoreyByGuid)));
}

/** `source.spaces`, kept to the storey the filter shows (spaces have no sub-category). */
export function visibleSpaces(spaces: readonly SolidSpace[] | null | undefined, state: ElementFilterState): SolidSpace[] {
  return (spaces ?? []).filter((s) => isElementVisible(state, 'space', s.storey_id));
}

/** `source.slabs`, kept to the categories (Dæk / Tag) and storey the filter shows. */
export function visibleSlabs(slabs: readonly SolidSlab[] | null | undefined, state: ElementFilterState): SolidSlab[] {
  return (slabs ?? []).filter((s) => isElementVisible(state, slabCategory(s.kind), s.storey_id));
}

/**
 * The "Bygningsdele" category an imported model layer's kind stands in for
 * (frontend-ayw.7's `ModelKind`), so the master/per-category toggle governs
 * an imported Revit/IFC model the same way it governs the detected one
 * (frontend-t02 acceptance criterion 3). `null` for a kind the panel has no
 * row for (column, circulation, ceiling, interior, services, other) — those
 * stay governed by `modelLayerOn` alone. An imported mesh is not split by
 * storey, so the storey filter never hides one.
 */
export function elementCategoryForImportedKind(kind: ModelKind): string | null {
  switch (kind) {
    case 'wall':
      return 'wall';
    case 'slab':
      return 'slab';
    case 'roof':
      return 'roof';
    case 'space':
      return 'space';
    case 'door':
      return 'door';
    case 'window':
      return 'window';
    default:
      return null;
  }
}
